import { useState } from "react";
import GenericActionBar from "../../../Layouts/GenericActionBar/GenericActionBar";
import Label from "../../../components/FormComponents/Label";
import useDirtyChecker from "../../../hooks/useDirtyChecker";
import { toastError, toastSuccess } from "../../../utils/toastUtils";
import axiosInstance, { handleApiError } from "../../../services/axiosService";
import Input from "../../../components/FormComponents/Input";
import { BASE_URL } from "../../../constant/constant";
import Textarea from "../../../components/FormComponents/Textarea";
import OCRQuestionsEditor from "./OCRQuestionsEditor";

const OcrTemplateEditor = ({ file, templateData, setTemplateData }) => {
  const { isDirty, markClean } = useDirtyChecker(templateData);
  const [isSaving, setIsSaving] = useState(false);

  const handleImageUploadSuccess = (imageName) => {
    if (!imageName) {
      toastError("Failed to upload image.");
      return;
    }
    setTemplateData({ ...templateData, image_name: imageName });
  };

  const handleChangeDescription = (e) => {
    setTemplateData({ ...templateData, description: e.target.value });
  };

  const handleSaveTemplate = async () => {
    try {
      setIsSaving(true);
      const payload = { ...templateData, name: file?.name };
      await axiosInstance.patch(`/ocr/${file.id}`, payload);
      toastSuccess("Template saved successfully");
      markClean();
    } catch (err) {
      handleApiError(err);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="d_flex flex_column">
      <GenericActionBar
        path={
          <div>
            <em>OCR Templates</em> → <span>{file.name}</span>
          </div>
        }
        actions={[
          {
            key: "save",
            icon: "save",
            title: isSaving ? "Saving..." : "Save",
            onClick: isSaving ? undefined : handleSaveTemplate
          }
        ]}
        options={{
          save: {
            className: `${isDirty ? "save_glowing" : ""} ${isSaving ? "disabled" : ""}`.trim()
          }
        }}
      />

      <div className="template_form_outer builder_form">
        <Label className="mb_2">Upload Image</Label>
        <ImageUploader imageName={templateData?.image_name} onSuccess={handleImageUploadSuccess} />

        <div className="mt_3">
          <Textarea label="Description" value={templateData?.description || ""} onChange={handleChangeDescription} />
        </div>

        <div className="mt_4">
          <OCRQuestionsEditor
            questions={templateData.questions || []}
            setQuestions={(updatedQuestions) => setTemplateData({ ...templateData, questions: updatedQuestions })}
          />
        </div>
      </div>
    </div>
  );
};

const ImageUploader = ({ imageName, onSuccess }) => {
  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) {
      toastError("Please select a valid image file.");
      return;
    }

    try {
      const formData = new FormData();
      formData.append("myFile", file);

      const { data } = await axiosInstance.post(`/public/upload`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });

      const { name } = data || {};
      if (name) {
        onSuccess(name);
      } else {
        toastError("Upload response did not return a valid image name.");
      }
    } catch (error) {
      handleApiError(error);
    }
  };

  return (
    <div className="flex_column gap_1">
      <Input type="file" name="image_data" onChange={handleFileChange} />
      {imageName && (
        <div style={{ height: "100px", width: "100px" }}>
          <img src={`${BASE_URL}/api/v1/public/${imageName}`} alt="Template Preview" className="w_100 h_100" />
        </div>
      )}
    </div>
  );
};

export default OcrTemplateEditor;
